import dynamic from 'next/dynamic';
import {
  createContext,
  Dispatch,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';

import { themes } from '@vimeo/iris/themes';

import useLocalStorage from '../hooks/use-storage/useLocalStorage';
import { GlobalStyles } from './GlobalStyles';

const BokehProvider = dynamic(async () => (await import('./BokehProvider')).BokehProvider);

// Theme shape type details
export type Mode = 'light' | 'dark';

export const THEME_PREFRENCE_KEY = 'theme-preference';

type Theme = any;

// Types for the reducer that controls the theme properties
type State = Theme;

type Action =
  | {
      type: 'TOGGLE';
    }
  | {
      type: 'INIT';
    }
  | {
      type: 'SET';
      payload: Theme;
    };

type ACTIONS = {
  [x: string]: (theme?: Theme) => Dispatch<Action> | void;
};

// A context for pulling down the dispatch to update the theme settings
export const ThemeDispatchContext = createContext<ACTIONS>({});

export function useThemeContext(): ACTIONS {
  return useContext(ThemeDispatchContext);
}

// The component that provides the theme to the application
type Props = {
  children: ReactNode;
  /** Whether to add theme provider at global level, defaults to true. */
  hasThemeProvider?: boolean;
  hasThemeSupport?: boolean;
  /** Whether the theme is persistent and changing it from the current page will update local storage. */
  isThemePersistent?: boolean;
  defaultTheme?: Mode;
  forceTheme?: Mode;
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'TOGGLE':
      return {
        ...state,
        ...(state.name === 'light' ? themes.dark : themes.light),
      };
    case 'SET':
      return {
        ...state,
        ...action.payload,
      };
    case 'INIT':
      return {
        ...state,
        isReady: true,
      };
    default:
      return state;
  }
}

const ThemeProvider = ({
  children,
  hasThemeProvider = true,
  hasThemeSupport = false,
  isThemePersistent = true,
  defaultTheme = 'light',
  forceTheme,
}: Props) => {
  const [themePreference, setThemePreference] = useLocalStorage<Mode>(
    THEME_PREFRENCE_KEY,
    defaultTheme,
  );

  const [state, dispatch] = useReducer(
    reducer,
    hasThemeSupport && themePreference === 'dark' ? { ...themes.dark } : { ...themes.light },
  );

  const actions = useMemo(
    () => ({
      toggleTheme: (theme?: Mode) => {
        dispatch({ type: 'TOGGLE' });
      },
      setTheme: (theme: Theme) => {
        dispatch({ type: 'SET', payload: theme });
      },
      initTheme: () => dispatch({ type: 'INIT' }),
    }),
    [dispatch],
  );

  useEffect(() => {
    // Remove pre rendered theme from body
    document.documentElement.hasAttribute('style') &&
      document.documentElement.removeAttribute('style');
    actions.initTheme();
  }, []);

  useEffect(() => {
    hasThemeSupport && isThemePersistent && setThemePreference(state.name);
  }, [hasThemeSupport, isThemePersistent, state.name, setThemePreference]);

  if (!hasThemeProvider) {
    return <>{children}</>;
  }

  return (
    <StyledThemeProvider theme={state}>
      <ThemeDispatchContext.Provider value={actions}>
        <GlobalStyles />
        <BokehProvider colorMode={forceTheme || state.name}>{children}</BokehProvider>
      </ThemeDispatchContext.Provider>
    </StyledThemeProvider>
  );
};

export { ThemeProvider };
